import {
  Alert,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import { strongSelectMenuProps } from "components/common/dropdownPaper";
import { rerunAwaitsConfirmation } from "features/integrationsBeta/clientRunStatus";
import {
  ClientRunStatusOverride,
  IntegrationRunListItem,
  useSetRunClientStatusMutation,
} from "features/integrationsBeta/integrationsBeta";
import { useGetClientRunStatusesQuery } from "features/integrationsBeta/platformEnums";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useState } from "react";
import { formatIsoDateTime } from "utils/dateUtils";
import { formatApiError } from "utils/formatApiError";
import RunClientStatus from "./RunClientStatus";
import RunStatusChip from "./RunStatusChip";

type Props = {
  open: boolean;
  run: IntegrationRunListItem | null;
  onClose: () => void;
  displayZone?: string;
  statusLabel: (value: string | null | undefined) => string;
  clientStatusLabel: (value: string | null | undefined) => string;
};

const AUTOMATIC = "";
/**
 * Pre-filled INTERNAL note when confirming after a successful re-run. Never a
 * client note: re-runs are internal, the client does not learn about them.
 */
const RERUN_NOTE_TEMPLATE = "Confirmed after a successful re-run.";
/** Same limit as NOTE_MAX_LENGTH in api/runs/serializers.py. */
const NOTE_MAX_LENGTH = 2000;

/**
 * "Set client status" (CAL-5776, CAL-6165): choose what the client sees for a
 * run - Automatic (derived) or a status the catalog marks settable - and keep
 * two optional, independent notes: an internal one only Calibrate reads, and,
 * behind a switch, one the client reads. Switched off means the client sees no
 * note. When the latest re-run succeeded and nobody has set a status yet, the
 * dialog pre-selects Success, so servicing a failed run is one click; an
 * existing override is a deliberate choice and is never pre-empted.
 */
export default function ClientStatusDialog({
  open,
  run,
  onClose,
  displayZone,
  statusLabel,
  clientStatusLabel,
}: Props) {
  const dispatch = useAppDispatch();
  const { data: clientStatuses } = useGetClientRunStatusesQuery();
  const [setClientStatus, { isLoading }] = useSetRunClientStatusMutation();
  const [override, setOverride] = useState<ClientRunStatusOverride | "">(
    AUTOMATIC,
  );
  const [internalNote, setInternalNote] = useState("");
  const [shareWithClient, setShareWithClient] = useState(false);
  const [clientNote, setClientNote] = useState("");

  const rerunSucceeded = !!run && rerunAwaitsConfirmation(run);
  const suggestConfirm = rerunSucceeded && run?.client_status_override === null;

  useEffect(() => {
    if (!open || !run) return;
    setOverride(
      suggestConfirm ? "SUCCESS" : (run.client_status_override ?? AUTOMATIC),
    );
    setInternalNote(
      suggestConfirm && !run.internal_note
        ? RERUN_NOTE_TEMPLATE
        : run.internal_note,
    );
    setShareWithClient(run.client_note !== "");
    setClientNote(run.client_note);
  }, [open, run, suggestConfirm]);

  const manual = override !== AUTOMATIC;
  const options = (clientStatuses ?? []).filter((o) => o.settable);
  const dropsSharedNote = !shareWithClient && !!run?.client_note;

  const handleSave = async () => {
    if (!run) return;
    try {
      await setClientStatus({
        runId: run.id,
        integrationId: run.integration,
        client_status_override: manual ? override : null,
        // Switched off = no note for the client, whatever was typed meanwhile.
        client_note: shareWithClient ? clientNote.trim() : "",
        internal_note: internalNote.trim(),
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: "Client status updated.",
        }),
      );
      onClose();
    } catch (err) {
      dispatch(
        pushNotification({
          severity: "error",
          message: formatApiError(
            (err as { data?: unknown })?.data,
            "Could not update the client status.",
          ),
        }),
      );
    }
  };

  if (!run) return null;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Set client status"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleSave}
            disabled={isLoading}
          >
            Save
          </CustomButton>
        </>
      }
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="body2" color="text.secondary">
            Run · {formatIsoDateTime(run.started_at, displayZone)}
          </Typography>
          <RunStatusChip status={run.status} label={statusLabel(run.status)} />
          <RunClientStatus run={run} clientStatusLabel={clientStatusLabel} />
        </Stack>

        {rerunSucceeded && (
          <Alert severity="info">
            The latest re-run of this run succeeded. Setting Success tells the
            client the data was delivered.
          </Alert>
        )}

        <TextField
          select
          size="small"
          label="Client status"
          value={override}
          onChange={(e) =>
            setOverride(e.target.value as ClientRunStatusOverride | "")
          }
          helperText={
            override === "FAILED"
              ? "The client sees this run as failed - a note for the client tells them why."
              : "What the client sees for this run."
          }
          // "" (Automatic) is a real choice: keep the label lifted and show
          // the option text instead of an empty field.
          InputLabelProps={{ shrink: true }}
          SelectProps={{ MenuProps: strongSelectMenuProps, displayEmpty: true }}
        >
          <MenuItem value={AUTOMATIC}>
            Automatic (currently {clientStatusLabel(run.client_status_derived)})
          </MenuItem>
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={shareWithClient}
                onChange={(e) => setShareWithClient(e.target.checked)}
              />
            }
            label="Share a note with the client"
          />
          {dropsSharedNote && (
            <Typography variant="caption" color="warning.dark">
              The client will no longer see the note saved for this run.
            </Typography>
          )}
          {shareWithClient && (
            <TextField
              multiline
              minRows={3}
              size="small"
              label="Note for the client"
              value={clientNote}
              onChange={(e) => setClientNote(e.target.value)}
              helperText="The client reads this next to the run's status - write it for them."
              inputProps={{ maxLength: NOTE_MAX_LENGTH }}
            />
          )}
        </Stack>

        <TextField
          multiline
          minRows={3}
          size="small"
          label="Internal note"
          value={internalNote}
          onChange={(e) => setInternalNote(e.target.value)}
          helperText="Only Calibrate sees this note. Optional."
          inputProps={{ maxLength: NOTE_MAX_LENGTH }}
        />

        {run.client_status_updated_at && (
          <Typography variant="caption" color="text.secondary">
            Last set by {run.client_status_updated_by || "Calibrate"} on{" "}
            {formatIsoDateTime(run.client_status_updated_at, displayZone)}.
          </Typography>
        )}
      </Stack>
    </AppDialog>
  );
}
